import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  User,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider
} from 'firebase/auth'
import { Capacitor } from '@capacitor/core'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { auth, googleProvider, appleProvider } from '../lib/firebase'
import { AuthState, AuthUser, CustomClaims } from '../types/auth'

interface AuthContextType extends AuthState {
  firebaseUser: User | null
  signInWithGoogle: () => Promise<void>
  signInWithGoogleRedirect: () => Promise<void>
  signInWithApple: () => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null
  })
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null)

  const transformFirebaseUser = async (firebaseUser: User | null): Promise<AuthUser | null> => {
    if (!firebaseUser) return null

    try {
      const tokenResult = await firebaseUser.getIdTokenResult()
      const claims = tokenResult.claims as CustomClaims

      console.log('🔐 User authentication info:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        claims: claims,
        orgId: claims.orgId,
        role: claims.role
      })

      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        orgId: claims.orgId,
        role: claims.role
      }
    } catch (error) {
      console.error('Error getting token result:', error)
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName
      }
    }
  }

  // ネイティブ（Capacitor iOS/Android）ではWKWebViewがWeb OAuthをブロックするため、
  // ネイティブのGoogleサインインを実行し、取得した資格情報でWeb SDKにサインインする。
  const signInWithGoogleNative = async () => {
    const result = await FirebaseAuthentication.signInWithGoogle()
    const idToken = result.credential?.idToken
    if (!idToken) {
      throw new Error('Google sign-in did not return an ID token')
    }
    const credential = GoogleAuthProvider.credential(idToken, result.credential?.accessToken)
    const userCredential = await signInWithCredential(auth, credential)

    // Custom Claimsが設定されるまで少し待つ
    setTimeout(async () => {
      try {
        await userCredential.user.getIdToken(true) // Force token refresh
      } catch (tokenError) {
        console.warn('Token refresh failed:', tokenError)
      }
    }, 1000)
  }

  const signInWithGoogle = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }))
        await signInWithGoogleNative()
      } catch (error) {
        console.error('Native Google sign-in failed:', error)
        setState(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Sign in failed'
        }))
      }
      return
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      const result = await signInWithPopup(auth, googleProvider)

      // Custom Claimsが設定されるまで少し待つ
      setTimeout(async () => {
        await result.user.getIdToken(true) // Force token refresh
      }, 1000)
    } catch (error) {
      console.warn('Popup sign-in failed, trying redirect method:', error)
      // Fallback to redirect method if popup fails
      try {
        await signInWithRedirect(auth, googleProvider)
      } catch (redirectError) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: redirectError instanceof Error ? redirectError.message : 'Sign in failed'
        }))
      }
    }
  }

  // Sign in with Apple。ネイティブ(iOS)ではネイティブのAppleサインインを実行し、
  // 取得した資格情報でWeb SDKにサインインする（WKWebViewのWeb OAuth制約を回避）。
  const signInWithAppleNative = async () => {
    const result = await FirebaseAuthentication.signInWithApple()
    const idToken = result.credential?.idToken
    if (!idToken) {
      throw new Error('Apple sign-in did not return an ID token')
    }
    const provider = new OAuthProvider('apple.com')
    const credential = provider.credential({
      idToken,
      rawNonce: result.credential?.nonce
    })
    const userCredential = await signInWithCredential(auth, credential)
    setTimeout(async () => {
      try {
        await userCredential.user.getIdToken(true)
      } catch (tokenError) {
        console.warn('Token refresh failed:', tokenError)
      }
    }, 1000)
  }

  const signInWithApple = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }))
        await signInWithAppleNative()
      } catch (error) {
        console.error('Native Apple sign-in failed:', error)
        setState(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Sign in failed'
        }))
      }
      return
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      const result = await signInWithPopup(auth, appleProvider)
      setTimeout(async () => {
        await result.user.getIdToken(true)
      }, 1000)
    } catch (error) {
      console.warn('Apple popup sign-in failed, trying redirect method:', error)
      try {
        await signInWithRedirect(auth, appleProvider)
      } catch (redirectError) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: redirectError instanceof Error ? redirectError.message : 'Sign in failed'
        }))
      }
    }
  }

  const signInWithGoogleRedirect = async () => {
    if (Capacitor.isNativePlatform()) {
      // ネイティブではリダイレクト方式は使えないため、ネイティブフローにフォールバック
      await signInWithGoogle()
      return
    }
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      await signInWithRedirect(auth, googleProvider)
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Redirect sign in failed'
      }))
    }
  }

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      const result = await createUserWithEmailAndPassword(auth, email, password)
      
      // Custom Claimsが設定されるまで少し待つ
      setTimeout(async () => {
        try {
          await result.user.getIdToken(true) // Force token refresh
        } catch (tokenError) {
          console.warn('Token refresh failed:', tokenError)
        }
      }, 1000)
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Sign up failed'
      }))
      throw error
    }
  }

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      await signInWithEmailAndPassword(auth, email, password)
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Sign in failed'
      }))
      throw error
    }
  }

  const signOut = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      if (Capacitor.isNativePlatform()) {
        try {
          await FirebaseAuthentication.signOut()
        } catch (nativeError) {
          console.warn('Native sign-out failed:', nativeError)
        }
      }
      await firebaseSignOut(auth)
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Sign out failed'
      }))
    }
  }

  const refreshUser = async () => {
    try {
      const currentUser = auth.currentUser
      if (currentUser) {
        // トークンを強制リフレッシュ
        await currentUser.getIdToken(true)
        // ユーザー情報を再取得
        const user = await transformFirebaseUser(currentUser)
        setState(prev => ({
          ...prev,
          user,
          loading: false,
          error: null
        }))
        console.log('🔄 User refreshed:', { orgId: user?.orgId, role: user?.role })
      }
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  useEffect(() => {
    // Handle redirect result on component mount
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth)
        if (result && result.user) {
          // Custom Claimsが設定されるまで少し待つ
          setTimeout(async () => {
            try {
              await result.user.getIdToken(true) // Force token refresh
            } catch (tokenError) {
              console.warn('Token refresh failed:', tokenError)
            }
          }, 1000)
        }
      } catch (error) {
        console.error('Redirect result error:', error)
        setState(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Redirect authentication failed'
        }))
      }
    }

    handleRedirectResult()

    const unsubscribe = onAuthStateChanged(auth, async (currentFirebaseUser) => {
      try {
        setFirebaseUser(currentFirebaseUser)
        const user = await transformFirebaseUser(currentFirebaseUser)
        setState({
          user,
          loading: false,
          error: null
        })
      } catch (error) {
        setFirebaseUser(null)
        setState({
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Authentication error'
        })
      }
    })

    return unsubscribe
  }, [])

  const value: AuthContextType = {
    ...state,
    firebaseUser,
    signInWithGoogle,
    signInWithGoogleRedirect,
    signInWithApple,
    signUpWithEmail,
    signInWithEmail,
    signOut,
    logout: signOut, // logoutはsignOutのエイリアス
    refreshUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}